import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Save, Radio, ShieldAlert, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function CallRecordingSettings() {
  const [settings, setSettings] = useState({ enabled: true, announce: true, announcement: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    api.get('/voice/recording-settings')
      .then(r => setSettings(r.data))
      .catch(() => toast.error('Could not load recording settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/voice/recording-settings', settings);
      setSettings(r.data);
      toast.success('Call recording settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/voice/recordings/sync');
      toast.success(`${r.data.recordings_found} recordings found · ${r.data.calls_updated} calls linked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <Card className="bg-white border-gray-100"><CardContent className="p-6 text-sm text-gray-400">Loading...</CardContent></Card>;
  }

  const publicBase = settings.public_base_url || '';

  return (
    <Card className="bg-white border-gray-100">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-gray-100 bg-gradient-to-r from-slate-50 to-white">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <Radio className="w-4 h-4 text-red-500" /> Record all calls
            </p>
            <p className="text-xs text-gray-500 max-w-lg">
              Records both incoming and outgoing calls (dual channel). Recorded calls get a
              player in Phone → Recent Calls and in each contact's Communications tab.
            </p>
          </div>
          <Switch
            data-testid="recording-enabled-switch"
            checked={!!settings.enabled}
            onCheckedChange={v => setSettings(s => ({ ...s, enabled: v }))}
          />
        </div>

        <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-amber-100 bg-amber-50/60">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" /> Play the recording notice
            </p>
            <p className="text-xs text-gray-600 max-w-lg">
              Florida is a two-party consent state (Fla. Stat. § 934.03). Both parties hear this
              notice before the call connects, which is what makes a recording usable as proof.
              Turning it off is not recommended.
            </p>
          </div>
          <Switch
            data-testid="recording-announce-switch"
            checked={!!settings.announce}
            onCheckedChange={v => setSettings(s => ({ ...s, announce: v }))}
          />
        </div>

        <div className="space-y-2">
          <Label>Recording notice text</Label>
          <Textarea
            data-testid="recording-announcement-input"
            rows={2}
            value={settings.announcement || ''}
            onChange={e => setSettings(s => ({ ...s, announcement: e.target.value }))}
            placeholder="This call may be recorded for quality and record-keeping purposes."
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="save-recording-settings" onClick={save} disabled={saving} className="bg-[#003366] hover:bg-[#004080]">
            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button data-testid="sync-recordings-btn" variant="outline" onClick={sync} disabled={syncing} className="border-[#003366]/20 text-[#003366]">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} /> Sync recordings from Twilio
          </Button>
        </div>

        <p className="text-xs text-gray-400 border-t border-gray-100 pt-4">
          Recording was disabled before this feature shipped, so calls made earlier have no
          audio to recover. The recording history starts from the moment you turn this on.
        </p>

        <div className="border-t border-gray-100 pt-4 space-y-2">
          <p className="text-sm font-semibold text-gray-800">Twilio webhooks</p>
          <p className="text-xs text-gray-500">
            For <strong>incoming</strong> calls to ring the browser, be recorded and show up in the
            history, set the Twilio phone number's "A call comes in" webhook to the URL below
            (HTTP POST). Outgoing calls already use the TwiML App voice URL.
          </p>
          {[
            { label: 'A call comes in', path: '/api/voice/incoming' },
            { label: 'TwiML App voice URL (outgoing)', path: '/api/voice/twiml' },
            { label: 'A message comes in', path: '/api/voice/sms-incoming' },
          ].map(w => (
            <div key={w.path} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-56 flex-shrink-0">{w.label}</span>
              <code className="flex-1 text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 truncate">
                {publicBase}{w.path}
              </code>
              <Button
                data-testid={`copy-webhook-${w.path.split('/').pop()}`}
                size="sm"
                variant="ghost"
                onClick={() => { navigator.clipboard.writeText(`${publicBase}${w.path}`); toast.success('Copied'); }}
              >
                Copy
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
